import moment from "moment";
import React, { useState } from "react";
import { Person as PersonIcon, SupervisedUserCircle as SupervisedUserCircleIcon } from "@material-ui/icons";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    PermissionActionType,
    IWorkspaceUserExtended,
    WorkspaceUserRole
} from "common/interfaces";
import { List, ListHeaderLine, ListLine } from "js/Components/List";
import WorkspaceController from "js/controllers/WorkspaceController";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import getLogger, { LogGroup } from "js/core/logger";
import { ShowErrorDialog, ShowSnackBar } from "js/react/components/Dialogs/BaseDialog";
import { BlueButton } from "js/react/components/UiComponents";
import { t } from "js/i18n";

/**
 * Logger shared by helpers in this module.
 */
const logger = getLogger(LogGroup.TEAMS);

/**
 * Shared layout containers
 */
export const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: white;
    padding: 20px 15px;
`;

export const PEOPLE_LIST_GRID_TEMPLATE_COLUMNS = "minmax(0, 2fr) 1fr 1fr 1fr 1fr 160px";

export const ToolsContainer = styled.div`
    padding-bottom: 20px;
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: center;
    justify-content: flex-end;
    background: white;
`;

export const PeopleListContainer = styled(Container)`
    gap: 0;
`;

export const PeopleList = styled(List)`
    gap: 0;

    && ${ListHeaderLine} {
        margin-bottom: 0;
        padding: 5px 20px;
        background: #eee;
        color: #666;
        font-size: 11px;
        letter-spacing: 0.5px;
        line-height: 14px;
    }
`;

export const PeopleListLine = styled(ListLine)`
    border-bottom: 1px solid #EEF1F3;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #F8FAFB;
    }
`;

export const PeopleListActionButton = styled(BlueButton)`
    &&& {
        height: auto;
    }
`;

const ViewSelectorContainer = styled.div`
    display: flex;
    align-items: center;
    align-self: stretch;
    gap: 6px;
    margin-right: auto;
`;

const ViewSelectorButton = styled.button<{ $active: boolean }>`
    display: flex;
    width: 150px;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    gap: 6px;
    padding: 8px 0;
    border: 0;
    border-bottom: 2px solid ${({ $active }) => $active ? "#11A9E2" : "transparent"};
    background: transparent;
    color: "#444";
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    cursor: pointer;
    opacity: 1;

    svg {
        color: ${({ $active }) => $active ? "#11A9E2" : "#888888"};
    }

    :hover {
        background: #F7FAFC;
    }
`;

export type PeoplePaneView = "members" | "groups";

export function PeoplePaneViewSelector({ selectedView, onChange }: {
    selectedView: PeoplePaneView;
    onChange: (view: PeoplePaneView) => void;
}) {
    return (
        <ViewSelectorContainer>
            <ViewSelectorButton $active={selectedView === "members"} onClick={() => onChange("members")}>
                <PersonIcon fontSize="small" />
                {t("Members")}
            </ViewSelectorButton>
            <ViewSelectorButton $active={selectedView === "groups"} onClick={() => onChange("groups")}>
                <SupervisedUserCircleIcon fontSize="small" />
                {t("Groups")}
            </ViewSelectorButton>
        </ViewSelectorContainer>
    );
}

/**
 * Shared permission helper used by lists and row components.
 */
export const canManageUsers = (): boolean =>
    WorkspaceController.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE] &&
    WorkspaceController.actionPermissions[ActionPermissionsObject.USERS][PermissionActionType.MANAGE];

/**
 * Sort-related types + hook
 */
export type SortDirection = "asc" | "desc";
export type SortFieldCommon = "name" | "role" | "lastActive";

export interface UseUserListSortingResult<TSortField extends string = SortFieldCommon> {
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    sort: SortDirection;
    sortBy: TSortField;
    setSort: (value: SortDirection) => void;
    setSortBy: (value: TSortField) => void;
    handleSearchBarChange: (query: string) => void;
    handleHeaderClick: (field: TSortField) => void;
}

/**
 * Shared hook for search + sort state.
 * Pro / Team lists can use this and specify their own sort-field union.
 */
export function useUserListSorting<TSortField extends string = SortFieldCommon>(
    defaultSortBy: TSortField = "name" as TSortField
): UseUserListSortingResult<TSortField> {
    const [searchQuery, setSearchQuery] = useState("");
    const [sort, setSort] = useState<SortDirection>("asc");
    const [sortBy, setSortBy] = useState<TSortField>(defaultSortBy);

    const handleSearchBarChange = (query: string) => {
        setSearchQuery(query);
    };

    const handleHeaderClick = (field: TSortField) => {
        if (sortBy === field) {
            setSort(sort === "asc" ? "desc" : "asc");
        } else {
            setSort("asc");
            setSortBy(field);
        }
    };

    return {
        searchQuery,
        setSearchQuery,
        sort,
        sortBy,
        setSort,
        setSortBy,
        handleSearchBarChange,
        handleHeaderClick
    };
}

/**
 * Shared text search filter.
 */
export function filterListItemBySearch<T extends { name: string }>(
    item: T,
    searchQuery: string
): boolean {
    if (!searchQuery) return true;
    return item.name.toLowerCase().includes(searchQuery.toLowerCase());
}

/**
 * Shared comparator for name / role / lastActive fields.
 * Pro / Team can inject their own role sort value via getRoleSortValue.
 */
export function compareListItems<T extends { name: string; lastActive?: string | number | Date }>(
    a: T,
    b: T,
    sortBy: SortFieldCommon,
    sort: SortDirection,
    getRoleSortValue?: (item: T) => string
): number {
    if (sortBy === "name") {
        const result = a.name.localeCompare(b.name);
        return sort === "asc" ? result : -result;
    }

    if (sortBy === "role" && getRoleSortValue) {
        const roleA = getRoleSortValue(a);
        const roleB = getRoleSortValue(b);
        const result = roleA.localeCompare(roleB);
        return sort === "asc" ? result : -result;
    }

    if (sortBy === "lastActive") {
        const lastActiveA = a.lastActive ? moment(a.lastActive).unix() : 0;
        const lastActiveB = b.lastActive ? moment(b.lastActive).unix() : 0;
        return sort === "asc"
            ? lastActiveA - lastActiveB
            : lastActiveB - lastActiveA;
    }

    return 0;
}

/**
 * Shared helper to build the "name" field from displayName + email.
 */
export function getFullUserDisplayName(
    displayName?: string | null,
    email?: string | null
): string {
    return `${displayName ?? ""} ${email ?? ""}`.trim();
}

/**
 * Shared helper to trigger CSV export of users + invites.
 */
export const downloadUsersAndInvites = (): void => {
    WorkspaceController.downloadUsersAndInvitesList();
};

/**
 * Shared helper to update a workspace user's role with progress dialog,
 * snackbar and error handling.
 *
 * `roleLabels` should be the plan-specific label map, e.g. Pro vs Team.
 */
export async function updateWorkspaceUserRoleWithDialog(
    user: IWorkspaceUserExtended,
    value: WorkspaceUserRole,
    roleLabels: Partial<Record<WorkspaceUserRole, string>>
): Promise<void> {
    const displayName = user.displayName ?? user.email ?? "";

    return withProgressDialog({
        title: t("Updating member..."),
        message: t("Please wait while we update the member..."),
        action: async () => {
            try {
                await WorkspaceController.updateWorkspaceUser({
                    uid: user.uid,
                    update: { role: value }
                });

                ShowSnackBar({
                    message: t("Member {{name}} updated to {{role}}", {
                        name: displayName,
                        role: roleLabels[value]
                    })
                });
            } catch (err: any) {
                logger.error(err, "[UserLine] Failed to update member");

                ShowErrorDialog({
                    title: t("Failed to update member"),
                    message: err.message
                });
            }
        }
    });
}
